import type { ICopyArticleCmd } from "@/data/commands/catalog/CopyArticleCmd";
import type { ICreateArticleCmd } from "@/data/commands/catalog/CreateArticleCmd";
import type { ICreateFulfillmentGroupCmd } from "@/data/commands/catalog/CreateFulfillmentGroupCmd";
import type { ICreateMenuCategoryCmd } from "@/data/commands/catalog/CreateMenuCategoryCmd";
import type { ICreateOfferCmd, IUpdateLinkedOffersCmd } from "@/data/commands/catalog/CreateOfferCmd";
import type { ICreateOfferScheduleCmd } from "@/data/commands/catalog/CreateOfferScheduleCmd";
import type { ICreateOptionGroupCmd } from "@/data/commands/catalog/CreateOptionGroupCmd";
import type { ICreateReportCategoryCmd } from "@/data/commands/catalog/CreateReportCategoryCmd";
import type { IExportStockCmd } from "@/data/commands/catalog/ExportStockCmd";
import type { IUpdateArticleStockCmd } from "@/data/commands/catalog/UpdateArticleStockCmd";
import type { IArticle, IArticleOptionGroup, IArticleProductionSiteAllocation, IFullArticle, IStock, IStockTableViewArticle, ITableViewArticle } from "@/data/model/Article";
import { Weekday } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { ILinkedOfferEntity } from "@/data/model/Location";
import type { IFullMenuCategory, IMenuCategory, IMenuCategoryWithOffers } from "@/data/model/MenuCategory";
import type { IOffer, IOfferWithScheduleState, ISortedArticle, ISortedCategory } from "@/data/model/Offer";
import type { IOfferSchedule, IOfferSchedulePreview, IScheduleShift } from "@/data/model/OfferSchedule";
import type { IOptionGroup } from "@/data/model/OptionGroup";
import type { IProductionSite } from "@/data/model/ProductionSite";
import type { IReportCategory } from "@/data/model/ReportCategory";
import type { ILinkableEntity } from "@/data/model/Shared";
import type { IArticlesQry } from "@/data/queries/catalog/ArticlesQry";
import type { IFulfillmentGroupsQry } from "@/data/queries/catalog/FulfillmentGroupsQry";
import type { IMenuCategoriesQry } from "@/data/queries/catalog/MenuCategoriesQry";
import type { IOfferDetailQry, IOfferQry } from "@/data/queries/catalog/OfferQry";
import type { IOfferSchedulePreviewQry } from "@/data/queries/catalog/OfferSchedulePreviewQry";
import type { IOfferSchedulesQry } from "@/data/queries/catalog/OfferSchedulesQry";
import type { IOptionGroupsQry } from "@/data/queries/catalog/OptionGroupsQry";
import type { IProductionSiteQry } from "@/data/queries/catalog/ProductionSiteQry";
import type { IReportCategoriesQry } from "@/data/queries/catalog/ReportCategoriesQry";
import { CurrentIdentityService } from "@/data/services/CurrentIdentityService";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";



import { BaseServiceDao, type IEditableDataSource, type IImageDataSource, type IMultiEntityLinkDataSource, type IPaginatedResult, parseCollection, parseMultiLanguageText, parsePaginatedResult } from "./BaseServiceDao";
import { PlanningDao } from "./PlanningDao";
import { RoomDao } from "./RoomDao";


export class CatalogDao extends BaseServiceDao {
    public static className = "CatalogDao";
    constructor() {
        super("catalog");
        this.isLocationDependent = true;
    }

    //article

    public async getArticle(id: string): Promise<IFullArticle> {
        return this.get("article/" + id, undefined, CatalogDao.parseFullArticle);
    }

    public async getArticles(
        qry: IArticlesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<ITableViewArticle>> {
        return this.get("article/", qry, (f) => parsePaginatedResult(f, CatalogDao.parseTableViewArticle), abortSignal);
    }

    public async getStockArticles(qry: IArticlesQry): Promise<IPaginatedResult<IStockTableViewArticle>> {
        return this.get("article/stock", qry, (f) => parsePaginatedResult(f, CatalogDao.parseStockTableViewArticle));
    }

    public async exportStock(cmd: IExportStockCmd) {
        return this.postDownloadFile("article/stock/export", cmd);
    }

    public async createArticle(cmd: ICreateArticleCmd): Promise<IFullArticle> {
        return this.post("article/", cmd, CatalogDao.parseFullArticle);
    }

    public async copyArticle(id: string, cmd: ICopyArticleCmd): Promise<IFullArticle> {
        return this.post(`article/${id}/copy`, cmd, CatalogDao.parseFullArticle);
    }

    public async updateArticle(id: string, cmd: ICreateArticleCmd): Promise<IFullArticle> {
        return this.put("article/" + id, cmd, CatalogDao.parseFullArticle);
    }

    public async removeArticle(id: string): Promise<void> {
        return this.delete("article/" + id, {});
    }

    public async setArticleImage(id: string, file: File): Promise<string> {
        return this.post("article/" + id + "/image", { Image: file }, (c) => c.path, true);
    }

    public async removeArticleImage(id: string): Promise<void> {
        return this.delete("article/" + id + "/image", {});
    }

    public async setArticleStock(id: string, cmd: IUpdateArticleStockCmd): Promise<IStockTableViewArticle> {
        return this.put("article/" + id + "/stock", cmd, CatalogDao.parseStockTableViewArticle);
    }

    public articleDataSource: IEditableDataSource<ITableViewArticle, IFullArticle> = {
        getData: (filter, abortSignal) => this.getArticles(filter, abortSignal),
        getSingle: (a) => this.getArticle(a),
        create: (a) => this.createArticle(a),
        update: (a, b) => this.updateArticle(a, b),
        remove: (a) => this.removeArticle(a),
    };

    public articleImageDataSource: IImageDataSource<IArticle> = {
        create: (m, file) => this.setArticleImage(m.id, file),
        remove: (m) => this.removeArticleImage(m.id),
        valueAccessor: (m, setter) => {
            if (setter !== undefined) {
                m.imagePath = setter == null ? undefined : setter;
            }
            return m.imagePath;
        },
    };

    //option group

    public async getOptionGroup(id: string): Promise<IOptionGroup> {
        return this.get("optiongroup/" + id, undefined, CatalogDao.parseOptionGroup);
    }

    public async getOptionGroups(
        qry: IOptionGroupsQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IOptionGroup>> {
        return this.get("optiongroup/", qry, (f) => parsePaginatedResult(f, CatalogDao.parseOptionGroup), abortSignal);
    }

    public async createOptionGroup(cmd: ICreateOptionGroupCmd): Promise<IOptionGroup> {
        return this.post("optiongroup/", cmd, CatalogDao.parseOptionGroup);
    }

    public async updateOptionGroup(id: string, cmd: ICreateOptionGroupCmd): Promise<IOptionGroup> {
        return this.put("optiongroup/" + id, cmd, CatalogDao.parseOptionGroup);
    }

    public async removeOptionGroup(id: string): Promise<void> {
        return this.delete("optiongroup/" + id, {});
    }

    public optionGroupDataSource: IEditableDataSource<IOptionGroup> = {
        getData: (filter, abortSignal) => this.getOptionGroups(filter, abortSignal),
        getSingle: (a) => this.getOptionGroup(a),
        create: (a) => this.createOptionGroup(a),
        update: (a, b) => this.updateOptionGroup(a, b),
        remove: (a) => this.removeOptionGroup(a),
    };

    //menu category

    public async getMenuCategory(id: string): Promise<IFullMenuCategory> {
        return this.get("menucategory/" + id, undefined, CatalogDao.parseFullMenuCategory);
    }

    public async getMenuCategories(
        qry: IMenuCategoriesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IFullMenuCategory>> {
        return this.get(
            "menucategory/",
            qry,
            (f) => parsePaginatedResult(f, CatalogDao.parseFullMenuCategory),
            abortSignal
        );
    }

    public async createMenuCategory(cmd: ICreateMenuCategoryCmd): Promise<IFullMenuCategory> {
        return this.post("menucategory/", cmd, CatalogDao.parseFullMenuCategory);
    }

    public async updateMenuCategory(id: string, cmd: ICreateMenuCategoryCmd): Promise<IFullMenuCategory> {
        return this.put("menucategory/" + id, cmd, CatalogDao.parseFullMenuCategory);
    }

    public async removeMenuCategory(id: string): Promise<void> {
        return this.delete("menucategory/" + id, {});
    }

    public async setMenuCategoryImage(id: string, file: File): Promise<string> {
        return this.post("menucategory/" + id + "/image", { Image: file }, (c) => c.path, true);
    }

    public async removeMenuCategoryImage(id: string): Promise<void> {
        return this.delete("menucategory/" + id + "/image", {});
    }

    public menuCategoryDataSource: IEditableDataSource<IFullMenuCategory> = {
        getData: (filter, abortSignal) => this.getMenuCategories(filter, abortSignal),
        getSingle: (a) => this.getMenuCategory(a),
        create: (a) => this.createMenuCategory(a),
        update: (a, b) => this.updateMenuCategory(a, b),
        remove: (a) => this.removeMenuCategory(a),
    };

    public menuCategoryImageDataSource: IImageDataSource<IMenuCategory> = {
        create: (m, file) => this.setMenuCategoryImage(m.id, file),
        remove: (m) => this.removeMenuCategoryImage(m.id),
        valueAccessor: (m, setter) => {
            if (setter !== undefined) m.imagePath = setter == null ? undefined : setter;

            return m.imagePath;
        },
    };

    public categorySearch: IMultiEntityLinkDataSource = {
        search: (e, t) =>
            this.get("menucategory/search", { Exclude: e, Term: t }, (f) =>
                parseCollection(f, CatalogDao.parseMultiLinkableEntity)
            ),
    };

    //report category

    public async getReportCategory(id: string): Promise<IReportCategory> {
        return this.get("reportcategory/" + id, undefined, CatalogDao.parseReportCategory);
    }

    public async getReportCategories(
        qry: IReportCategoriesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IReportCategory>> {
        return this.get(
            "reportcategory/",
            qry,
            (f) => parsePaginatedResult(f, CatalogDao.parseReportCategory),
            abortSignal
        );
    }

    public async getNonVouchersReportCategories(
        qry: IReportCategoriesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IReportCategory>> {
        const queryWithoutValueVoucherSalesCategory = { ...qry, excludeValueVoucherSalesCategory: true };
        return this.getReportCategories(queryWithoutValueVoucherSalesCategory, abortSignal);
    }

    public async createReportCategory(cmd: ICreateReportCategoryCmd): Promise<IReportCategory> {
        return this.post("reportcategory/", cmd, CatalogDao.parseReportCategory);
    }

    public async updateReportCategory(id: string, cmd: ICreateReportCategoryCmd): Promise<IReportCategory> {
        return this.put("reportcategory/" + id, cmd, CatalogDao.parseReportCategory);
    }

    public async removeReportCategory(id: string): Promise<void> {
        return this.delete("reportcategory/" + id, {});
    }

    public reportCategoryDataSource: IEditableDataSource<IReportCategory> = {
        getData: (filter, abortSignal) => this.getReportCategories(filter, abortSignal),
        getSingle: (a) => this.getReportCategory(a),
        create: (a) => this.createReportCategory(a),
        update: (a, b) => this.updateReportCategory(a, b),
        remove: (a) => this.removeReportCategory(a),
    };

    public nonVouchersReportCategoryDataSource: IEditableDataSource<IReportCategory> = {
        getData: (filter, abortSignal) => this.getNonVouchersReportCategories(filter, abortSignal),
        getSingle: (a) => this.getReportCategory(a),
        create: (a) => this.createReportCategory(a),
        update: (a, b) => this.updateReportCategory(a, b),
        remove: (a) => this.removeReportCategory(a),
    };

    //fulfillment group

    public async getFulfillmentGroup(id: string): Promise<IFulfillmentGroup> {
        return this.get("fulfillmentgroup/" + id, undefined, CatalogDao.parseFulfillmentGroup);
    }

    public async getFulfillmentGroups(
        qry: IFulfillmentGroupsQry = {
            includeFromChildLocations: false,
        },
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IFulfillmentGroup>> {
        return this.get(
            "fulfillmentgroup/",
            qry,
            (f) => parsePaginatedResult(f, CatalogDao.parseFulfillmentGroup),
            abortSignal
        );
    }

    public async createFulfillmentGroup(cmd: ICreateFulfillmentGroupCmd): Promise<IFulfillmentGroup> {
        return this.post("fulfillmentgroup/", cmd, CatalogDao.parseFulfillmentGroup);
    }

    public async updateFulfillmentGroup(id: string, cmd: ICreateFulfillmentGroupCmd): Promise<IFulfillmentGroup> {
        return this.put("fulfillmentgroup/" + id, cmd, CatalogDao.parseFulfillmentGroup);
    }

    public async removeFulfillmentGroup(id: string): Promise<void> {
        return this.delete("fulfillmentgroup/" + id, {});
    }

    public fulfillmentGroupDataSource: IEditableDataSource<IFulfillmentGroup> = {
        getData: (filter, abortSignal) => {
            return this.getFulfillmentGroups({
                includeFromChildLocations: false,
                ...filter
            }, abortSignal);
        },
        getSingle: (a) => this.getFulfillmentGroup(a),
        create: (a) => this.createFulfillmentGroup(a),
        update: (a, b) => this.updateFulfillmentGroup(a, b),
        remove: (a) => this.removeFulfillmentGroup(a),
    };

    //offer

    public async getOffer(id: string): Promise<IOffer> {
        return this.get("offer/" + id, undefined, CatalogDao.parseOffer);
    }

    public async getOffers(qry: IOfferQry = {
        includeChildOffers: false
    }, abortSignal?: AbortSignal): Promise<IPaginatedResult<IOffer>> {
        return this.get("offer/", qry, (f) => parsePaginatedResult(f, CatalogDao.parseOffer), abortSignal);
    }

    public async updateLinkedOffers(id: string, cmd: IUpdateLinkedOffersCmd): Promise<Array<ILinkedOfferEntity>> {
        return this.post(`offer/${id}/linked-offer`, cmd, (c) => parseCollection(c, CatalogDao.parseLinkedOffer));
    }

    public async getOffersWithSchedule(
        qry: IOfferQry = {
            includeChildOffers: false
        },
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IOfferWithScheduleState>> {
        return this.get(
            "offer/schedule/",
            qry,
            (f) => parsePaginatedResult(f, CatalogDao.parseOfferWithScheduleState),
            abortSignal
        );
    }

    public async createOffer(cmd: ICreateOfferCmd): Promise<IOffer> {
        return this.post("offer/", cmd, CatalogDao.parseOffer);
    }

    public async updateOffer(id: string, cmd: ICreateOfferCmd): Promise<IOffer> {
        return this.put("offer/" + id, cmd, CatalogDao.parseOffer);
    }

    public async removeOffer(id: string): Promise<void> {
        return this.delete("offer/" + id, {});
    }

    public async activateOfferForAllOrderApplications(id: string, roomId: string): Promise<IOfferWithScheduleState> {
        return this.post("offer/" + id + "/activate-all", { roomId: roomId }, CatalogDao.parseOfferWithScheduleState);
    }

    public async activateOfferForOrderApplication(
        id: string,
        roomId: string,
        orderUseCaseId: string
    ): Promise<IOfferWithScheduleState> {
        return this.post(
            "offer/" + id + "/activate",
            { roomId: roomId, orderUseCaseId: orderUseCaseId },
            CatalogDao.parseOfferWithScheduleState
        );
    }

    public async deactivateOfferAllOrderApplications(id: string, roomId: string): Promise<IOfferWithScheduleState> {
        return this.post("offer/" + id + "/deactivate-all", { roomId: roomId }, CatalogDao.parseOfferWithScheduleState);
    }

    public async deactivateOfferForOrderApplication(
        id: string,
        roomId: string,
        orderUseCaseId: string
    ): Promise<IOfferWithScheduleState> {
        return this.post(
            "offer/" + id + "/deactivate",
            { roomId: roomId, orderUseCaseId: orderUseCaseId },
            CatalogDao.parseOfferWithScheduleState
        );
    }

    public async resetOfferActivations(id: string): Promise<IOfferWithScheduleState> {
        return this.post("offer/" + id + "/resetactivations", {}, CatalogDao.parseOfferWithScheduleState);
    }

    public async getOfferArticles(offerId: string): Promise<Array<ISortedCategory>> {
        return this.get(`offer/${offerId}/article`, (c) => parseCollection(c, CatalogDao.parseSortedCategory));
    }

    public async setCategoryPosition(offerId: string, categoryId: string, newIndex: number, id: string): Promise<void> {
        return this.put("offer/" + offerId + "/category/" + categoryId + "/move", { NewIndex: newIndex, id: id });
    }

    public async setArticleCategoryPosition(
        offerId: string,
        categoryId: string,
        articleId: string,
        id: string,
        newIndex: number
    ): Promise<void> {
        return this.put(`offer/${offerId}/category/${categoryId}/article/${articleId}/move`, {
            NewIndex: newIndex,
            articleAllocationId: id,
        });
    }

    public offerDataSource: IEditableDataSource<IOffer> = {
        getData: (filter, abortSignal) => this.getOffers({
            includeChildOffers: false,
                ...filter
        }, abortSignal),
        getSingle: (a) => this.getOffer(a),
        create: (a) => this.createOffer(a),
        update: (a, b) => this.updateOffer(a, b),
        remove: (a) => this.removeOffer(a),
    };

    public offerWithScheduleDataSource: IEditableDataSource<IOfferWithScheduleState, IOffer> = {
        getData: (filter, abortSignal) => this.getOffersWithSchedule({
            includeChildOffers: false
            }, abortSignal),
        getSingle: (a) => this.getOffer(a),
        create: (a) => this.createOffer(a),
        update: (a, b) => this.updateOffer(a, b),
        remove: (a) => this.removeOffer(a),
    };

    public offerSearch: IMultiEntityLinkDataSource = {
        search: (e, t) =>
            this.get("offer/search", { Exclude: e, Term: t }, (f) =>
                parseCollection(f, CatalogDao.parseLinkableEntity)
            ),
    };

    //offer schedules
    public async getOfferSchedule(id: string): Promise<IOfferSchedule> {
        return this.get("offerschedule/" + id, undefined, CatalogDao.parseOfferSchedule);
    }

    public async getOfferSchedules(
        qry: IOfferSchedulesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IOfferSchedule>> {
        return this.get(
            "offerschedule/",
            qry,
            (f) => parsePaginatedResult(f, CatalogDao.parseOfferSchedule),
            abortSignal
        );
    }

    public async createOfferSchedule(cmd: ICreateOfferScheduleCmd): Promise<IOfferSchedule> {
        return this.post("offerschedule/", cmd, CatalogDao.parseOfferSchedule);
    }

    public async updateOfferSchedule(id: string, cmd: ICreateOfferScheduleCmd): Promise<IOfferSchedule> {
        return this.put("offerschedule/" + id, cmd, CatalogDao.parseOfferSchedule);
    }

    public async removeOfferSchedule(id: string): Promise<void> {
        return this.delete("offerschedule/" + id, {});
    }

    public async getOfferSchedulePreview(qry: IOfferSchedulePreviewQry): Promise<Array<IOfferSchedulePreview>> {
        return this.get("offerschedule/preview", qry, (f) => parseCollection(f, CatalogDao.parseOfferSchedulePreview));
    }

    public offerScheduleDataSource: IEditableDataSource<IOfferSchedule> = {
        getData: (filter, abortSignal) => this.getOfferSchedules(filter, abortSignal),
        getSingle: (a) => this.getOfferSchedule(a),
        create: (a) => this.createOfferSchedule(a),
        update: (a, b) => this.updateOfferSchedule(a, b),
        remove: (a) => this.removeOfferSchedule(a),
    };

    //production site

    public async getProductionSite(id: string): Promise<IProductionSite> {
        return this.get("productionsite/" + id, undefined, CatalogDao.parseProductionSite);
    }

    public async getProductionSites(
        qry: IProductionSiteQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IProductionSite>> {
        return this.get(
            "productionsite/",
            qry,
            (f) => parsePaginatedResult(f, CatalogDao.parseProductionSite),
            abortSignal
        );
    }

    public async createProductionSite(cmd: ICreateOfferCmd): Promise<IProductionSite> {
        return this.post("productionsite/", cmd, CatalogDao.parseProductionSite);
    }

    public async updateProductionSite(id: string, cmd: ICreateOfferCmd): Promise<IProductionSite> {
        return this.put("productionsite/" + id, cmd, CatalogDao.parseProductionSite);
    }

    public async removeProductionSite(id: string): Promise<void> {
        return this.delete("productionsite/" + id, {});
    }

    public async testPrint(id: string): Promise<void> {
        return this.post("productionsite/" + id + "/testprint", {});
    }

    public async testPrintInterimBill(id: string): Promise<void> {
        return this.post("productionsite/" + id + "/test-print-interim-bill", {});
    }

    public async testPrintPaymentReceipt(id: string): Promise<void> {
        return this.post("productionsite/" + id + "/test-print-payment-receipt", {});
    }

    public async testPrintOrderReceipt(id: string): Promise<void> {
        return this.post("productionsite/" + id + "/test-print-order-receipt", {});
    }

    public productionSiteDataSource: IEditableDataSource<IProductionSite> = {
        getData: (filter, abortSignal) => this.getProductionSites(filter, abortSignal),
        getSingle: (a) => this.getProductionSite(a),
        create: (a) => this.createProductionSite(a),
        update: (a, b) => this.updateProductionSite(a, b),
        remove: (a) => this.removeProductionSite(a),
    };

    public productionSiteSearch: IMultiEntityLinkDataSource = {
        search: (e, t) =>
            this.get("productionsite/search", { Exclude: e, Term: t }, (f) =>
                parseCollection(f, CatalogDao.parseLinkableEntity)
            ),
    };

    public static parseArticle(literal: any): IArticle {
        return {
            id: literal.id,
            locationId: literal.locationId,
            isAvailable: literal.isAvailable,
            name: parseMultiLanguageText(literal.name),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            internalName: literal.internalName == null ? undefined : literal.internalName,
            price: parseFloat(literal.price),
            oohPrice: literal.oohPrice == null ? undefined : parseFloat(literal.oohPrice),
            unavailabilityReason: parseInt(literal.unavailabilityReason),
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
            isOpenPriceEnabled: literal.isOpenPriceEnabled,
            isValueVoucher: literal.isValueVoucher,
            voucherValue: literal.voucherValue == null ? undefined : parseFloat(literal.voucherValue),
            canHaveComments: literal.canHaveComments,
        };
    }

    public static parseStockTableViewArticle(literal: any): IStockTableViewArticle {
        return {
            ...CatalogDao.parseArticle(literal),
            stock: CatalogDao.parseStock(literal.stock),
        };
    }

    public static parseTableViewArticle(literal: any): ITableViewArticle {
        return {
            ...CatalogDao.parseArticle(literal),
            offers: parseCollection(literal.offers, CatalogDao.parseOffer),
            categories: parseCollection(literal.categories, CatalogDao.parseMenuCategory),
            productionSites: parseCollection(literal.productionSites, CatalogDao.parseArticleProductionSiteAllocation),
            optionGroups: parseCollection(literal.optionGroups, CatalogDao.parseArticleOptionGroup),
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
        };
    }

    public static parseFullArticle(literal: any): IFullArticle {
        return {
            ...CatalogDao.parseTableViewArticle(literal),
            categories: parseCollection(literal.categories, CatalogDao.parseMenuCategoryWithOffers),
            shortDescription: parseMultiLanguageText(literal.shortDescription),
            longDescription: parseMultiLanguageText(literal.longDescription),
            reportCategoryId: literal.reportCategoryId == null ? undefined : literal.reportCategoryId,
            fulfillmentGroupId: literal.fulfillmentGroupId == null ? undefined : literal.fulfillmentGroupId,
            locationId: literal.locationId,
            vat: literal.vat == null ? undefined : parseFloat(literal.vat),
            oohVat: literal.oohVat == null ? undefined : parseFloat(literal.oohVat),
            minAge: literal.minAge == null ? undefined : parseInt(literal.minAge),
            stock: literal.stock == null ? undefined : CatalogDao.parseStock(literal.stock),
            maxQuantityIncOptionGroups:
                literal.maxQuantityIncOptionGroups == null ? undefined : parseInt(literal.maxQuantityIncOptionGroups),
        };
    }

    public static parseMenuCategory(literal: any): IMenuCategory {
        return {
            id: literal.id,
            isAvailable: literal.isAvailable,
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            name: parseMultiLanguageText(literal.name),
        };
    }

    public static parseMenuCategoryWithOffers(literal: any): IMenuCategoryWithOffers {
        return {
            ...CatalogDao.parseMenuCategory(literal),
            offers: parseCollection(literal.offers, CatalogDao.parseOffer),
        };
    }

    public static parseFullMenuCategory(literal: any): IFullMenuCategory {
        return {
            ...CatalogDao.parseMenuCategoryWithOffers(literal),
            description: parseMultiLanguageText(literal.description),
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
            locationId: literal.locationId,
        };
    }

    public static parseReportCategory(literal: any): IReportCategory {
        return {
            id: literal.id,
            locationId: literal.locationId,
            name: literal.name,
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
            order: literal.order == null ? undefined : parseInt(literal.order),
        };
    }

    public static parseFulfillmentGroup(literal: any): IFulfillmentGroup {
        return {
            id: literal.id,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
            fulfillmentTime: parseInt(literal.fulfillmentTime),
            order: literal.order == null ? undefined : parseInt(literal.order),
            maxUnfulfilledOrders:
                literal.maxUnfulfilledOrders == null ? undefined : parseInt(literal.maxUnfulfilledOrders),
        };
    }

    public static parseArticleProductionSiteAllocation(literal: any): IArticleProductionSiteAllocation {
        return {
            id: literal.id,
            productionSite: CatalogDao.parseProductionSite(literal.productionSite),
            roomIds: parseCollection(literal.roomIds, (c) => c),
        };
    }

    public static parseProductionSite(literal: any): IProductionSite {
        return {
            id: literal.id,
            locationId: literal.locationId,
            isActive: literal.isActive,
            lastCommunication:
                literal.lastCommunication == null ? undefined : DateTime.parseIsoDate(literal.lastCommunication),
            name: literal.name,
            printerProtocolType: parseInt(literal.printerProtocolType),
            printLayout: parseInt(literal.printLayout),
            largerFontSize: literal.largerFontSize,
            state: parseInt(literal.state),
            notifyOnFailEmail: literal.notifyOnFailEmail == null ? undefined : literal.notifyOnFailEmail,
            backupProductionSiteId: literal.backupProductionSiteId == null ? undefined : literal.backupProductionSiteId,
            printFromRooms: parseCollection(literal.printFromRooms, RoomDao.parseRoom),
            joinPrintingItemsMode: parseInt(literal.joinPrintingItemsMode),
            printReceiptsFromAllRooms: literal.printReceiptsFromAllRooms,
            alwaysPrintOutBillForOperatorPayments: literal.alwaysPrintOutBillForOperatorPayments,
        };
    }

    public static parseOptionGroup(literal: any): IOptionGroup {
        return {
            id: literal.id,
            locationId: literal.locationId,
            displayName: literal.displayName,
            internalName: literal.internalName == null ? undefined : literal.internalName,
            name: parseMultiLanguageText(literal.name),
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
            maxPerOption: literal.maxPerOption != null ? parseInt(literal.maxPerOption) : undefined,
            minOptions: literal.minOptions != null ? parseInt(literal.minOptions) : undefined,
            maxSum: literal.maxSum != null ? parseInt(literal.maxSum) : undefined,
            maxOptions: literal.maxOptions != null ? parseInt(literal.maxOptions) : undefined,
            enableAdvancedSettings: literal.enableAdvancedSettings,
            options: parseCollection(literal.options, (c) => {
                return {
                    article: CatalogDao.parseArticle(c.article),
                    price: c.price == null ? undefined : parseFloat(c.price),
                    order: parseInt(c.order),
                    preselected: c.preselected,
                };
            }),
            articles: parseCollection(literal.articles, (c) => {
                return {
                    article: CatalogDao.parseArticle(c.article),
                    order: c.order == null ? undefined : parseInt(c.order),
                };
            }),
            appearsInAllOffers: literal.appearsInAllOffers,
            appearsInOffers: parseCollection(literal.appearsInOffers, CatalogDao.parseOffer),
            appearsInAllUseCases: literal.appearsInAllUseCases,
            appearsInUseCases: parseCollection(literal.appearsInUseCases, PlanningDao.parseUseCase),
        };
    }

    public static parseOffer(literal: any): IOffer {
        return {
            id: literal.id,
            locationId: literal.locationId,
            name: literal.name,
            isActive: literal.isActive,
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
            linkedOffers: parseCollection(literal.linkedOffers, CatalogDao.parseLinkedOffer),
        };
    }

    public static parseLinkedOffer(literal: any): ILinkedOfferEntity {
        return {
            id: literal.id,
            parentOfferId: literal.parentOfferId,
            childLocationId: literal.childLocationId,
            linkedLocationId: literal.linkedLocationId,
            offerId: literal.offerId,
        };
    }

    public static parseOfferWithScheduleState(literal: any): IOfferWithScheduleState {
        return {
            ...CatalogDao.parseOffer(literal),
            currentPlanning: parseCollection(literal.currentPlanning, CatalogDao.parseOfferSchedulePreview),
        };
    }

    public static parseOfferSchedule(literal: any): IOfferSchedule {
        return {
            id: literal.id,
            locationId: literal.locationId,
            shifts: parseCollection(literal.shifts, CatalogDao.parseScheduleShift),
            offer: literal.offer != null ? CatalogDao.parseOffer(literal.offer) : undefined,
            rooms: parseCollection(literal.rooms, RoomDao.parseRoom),
            orderUseCases: parseCollection(literal.orderUseCases, RoomDao.parseRoomOrderUseCase),
            scheduleType: parseInt(literal.scheduleType),
            from: literal.from != null ? DateTime.parseIsoDate(literal.from) : undefined,
            until: literal.until != null ? DateTime.parseIsoDate(literal.until) : undefined,
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
        };
    }

    public static parseScheduleShift(literal: any): IScheduleShift {
        return {
            from: literal.from == null ? undefined : literal.from,
            until: literal.until == null ? undefined : literal.until,
            weekdays:
                literal.weekdays == null ? [] : parseCollection<Weekday>(literal.weekdays, (c) => (Weekday as any)[c]),
            wholeDay: literal.wholeDay,
            preorderTimeInSeconds: literal.preorderTimeInSeconds == null ? undefined : literal.preorderTimeInSeconds,
            fulfilmentTimeInterval: literal.fulfilmentTimeInterval,
            defaultCapacityPerFulfilmentTime:
                literal.defaultCapacityPerFulfilmentTime == null
                    ? undefined
                    : parseInt(literal.defaultCapacityPerFulfilmentTime),
        };
    }

    public static parseOfferSchedulePreview(literal: any): IOfferSchedulePreview {
        return {
            id: literal.id,
            isAllDay: literal.isAllDay,
            offerId: literal.offerId == null ? undefined : literal.offerId,
            offerName: literal.offerName,
            roomOrder: literal.roomOrder == null ? undefined : parseInt(literal.roomOrder),
            roomId: literal.roomId,
            roomName: literal.roomName,
            start: DateTime.parseIsoDate(literal.start),
            end: DateTime.parseIsoDate(literal.end),
            isClosedTime: literal.isClosedTime,
            orderUseCaseId: literal.orderUseCaseId,
            orderUseCaseName: parseMultiLanguageText(literal.orderUseCaseName),
            isForAllUseCases: literal.isForAllUseCases,
        };
    }

    public static parseSortedCategory(literal: any): ISortedCategory {
        return {
            id: literal.id,
            offerId: literal.offerId,
            order: literal.order == null ? undefined : parseInt(literal.order),
            category: CatalogDao.parseMenuCategory(literal.category),
            articles: parseCollection(literal.articles, CatalogDao.parseSortedArticle),
            isLinked: literal.isLinked,
            linkedToOfferId: literal.linkedToOfferId == null ? undefined : literal.linkedToOfferId,
        };
    }

    public static parseSortedArticle(literal: any): ISortedArticle {
        return {
            id: literal.id,
            offerId: literal.offerId,
            order: literal.order == null ? undefined : parseInt(literal.order),
            article: CatalogDao.parseArticle(literal.article),
            isLinked: literal.isLinked,
            linkedToOfferId: literal.linkedToOfferId == null ? undefined : literal.linkedToOfferId,
        };
    }

    public static parseArticleOptionGroup(literal: any): IArticleOptionGroup {
        return {
            id: literal.id,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            order: parseInt(literal.order),
            maxPerOption: literal.maxPerOption == null ? undefined : parseInt(literal.maxPerOption),
        };
    }

    public static parseLinkableEntity(literal: any): ILinkableEntity {
        return {
            id: literal.id,
            name: literal.name,
            appearsIn:
                literal.appearsIn != null
                    ? parseCollection(literal.appearsIn, CatalogDao.parseLinkableEntity)
                    : undefined,
        };
    }

    public static parseMultiLinkableEntity(literal: any): ILinkableEntity {
        return {
            id: literal.id,
            name: Format.translate(literal.name, CurrentIdentityService.getLanguage()),
            appearsIn:
                literal.appearsIn != null
                    ? parseCollection(literal.appearsIn, CatalogDao.parseLinkableEntity)
                    : undefined,
        };
    }

    public static parseStock(literal: any): IStock | undefined {
        if (literal == null || literal === undefined) return undefined;

        return {
            remaining: parseInt(literal.remaining),
            safetyStock: literal.safetyStock == null ? undefined : parseInt(literal.safetyStock),
            notifyThreshold: literal.notifyThreshold == null ? undefined : parseInt(literal.notifyThreshold),
        };
    }
}
